import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGrid, trTR } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getAllComponents, getUserBasedComponentAuthorizedUsers, updateUserBasedComponentsAuthorization } from 'src/store/slices/authorizationSlice';
import {
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';
import { SelectableTiles, parseRoleIds } from 'src/components/RoleAssignmentPanel';
import { isSessionUser, refreshUserRoles } from 'src/utils/roleSync';

const gridLocale = { ...trTR.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: 'Kayıt bulunamadı.' };
const SEARCH_FIELDS = ['ADSOYAD', 'SICILNO'];

function UserBasedComponentAuth() {
  const classes = useDynamicStyles();
  const dispatch = useDispatch();
  const [userList, setUserList] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [allComponents, setAllComponents] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selected, setSelected] = useState(new Set());   // Seçilen kullanıcının yetkili olduğu component ID'leri
  const [original, setOriginal] = useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [componentsLoading, setComponentsLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const getAllUserData = () => {
    setLoading(true);
    dispatch(getUserBasedComponentAuthorizedUsers()).then((response) => {
      if (response.meta?.requestStatus === 'fulfilled' && Array.isArray(response.payload)) {
        setUserList(response.payload);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    })
  }

  const getAllComponentsData = () => {
    setComponentsLoading(true);
    dispatch(getAllComponents()).then((response) => {
      if (response.payload?.length > 0) {
        setAllComponents(response.payload);
      }
      setComponentsLoading(false);
    });
  }

  useEffect(() => {
    getAllUserData();
    getAllComponentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const componentNames = useMemo(
    () => allComponents.reduce((acc, c) => ({ ...acc, [c.ID]: c.COMPONENT_NAME }), {}),
    [allComponents]
  );
  const componentItems = useMemo(
    () => allComponents.map((c) => ({ id: c.ID, label: c.COMPONENT_NAME })),
    [allComponents]
  );

  const handleUpdateRoleClick = (row) => {
    // Sadece tanımlı component'ler dikkate alınır
    const ids = new Set(parseRoleIds(row.ActiveComponents).filter((id) => componentNames[id] !== undefined));
    setSelectedUser(row);
    setSelected(new Set(ids));
    setOriginal(ids);
  };

  const closeDialog = () => {
    if (saving) return;
    setSelectedUser(null);
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const changeCount = componentItems.filter((c) => selected.has(c.id) !== original.has(c.id)).length;

  const handleUpdateUserAuthorization = () => {
    const data = {
      UserPermissions: allComponents.filter((c) => selected.has(c.ID)).map((c) => c.ID).toString(),
      UserId: selectedUser.ID
    }
    const editedSicil = selectedUser.SICILNO;
    setSaving(true);
    dispatch(updateUserBasedComponentsAuthorization(data)).then(() => {
      setSaving(false);
      setSelectedUser(null);
      getAllUserData();
      // Kullanıcı kendi yetkisini değiştirdiyse F5 gerekmeden güncellensin
      if (isSessionUser(editedSicil)) refreshUserRoles();
    })
  }

  const rows = useMemo(() => filterDynamicRows(userList, { searchText: search, searchFields: SEARCH_FIELDS }), [userList, search]);

  const columns = [
    { field: 'ADSOYAD', headerName: 'Ad Soyad', flex: 1, minWidth: 180 },
    { field: 'SICILNO', headerName: 'Sicil No', width: 130 },
    {
      field: 'ActiveComponents', headerName: 'Yetkili Olduğu İşlemler', flex: 1.6, minWidth: 260, sortable: false,
      renderCell: (p) => {
        const ids = parseRoleIds(p.value).filter((id) => componentNames[id] !== undefined);
        if (ids.length === 0) return <Box sx={{ fontSize: '11px', color: '#b0bec5' }}>Yetki yok</Box>;
        const shown = ids.slice(0, 2);
        const rest = ids.slice(2);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', overflow: 'hidden' }}>
            {shown.map((id) => (
              <Box key={id} sx={{ px: 1, height: 20, lineHeight: '20px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                {componentNames[id]}
              </Box>
            ))}
            {rest.length > 0 && (
              <Tooltip title={rest.map((id) => componentNames[id]).join(', ')}>
                <Box sx={{ px: 0.75, height: 20, lineHeight: '20px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, backgroundColor: '#eceff1', color: '#546e7a' }}>
                  +{rest.length}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: 'actions', headerName: 'İşlem', width: 80, align: 'center', headerAlign: 'center',
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <Tooltip title="Yetkileri düzenle">
          <span>
            <IconButton size="small" disabled={componentsLoading} onClick={() => handleUpdateRoleClick(p.row)}>
              <EditIcon sx={{ fontSize: 18, color: componentsLoading ? undefined : ORANGE }} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }
  ];

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Kullanıcı Bazlı İşlem Yetkilendirme"
          subheader={loading ? 'Yükleniyor…' : `${userList.length} kullanıcı · ${allComponents.length} işlem`}
        />
        <DynamicListToolbar
          searchText={search}
          onSearchChange={setSearch}
          placeholder="Ad soyad veya sicil no ara"
          shownCount={rows.length}
        />
        <DataGrid
          className={classes.grid}
          style={{ height: 560, width: '100%' }}
          columns={columns}
          rows={rows}
          loading={loading}
          rowHeight={44}
          headerHeight={44}
          disableSelectionOnClick
          getRowId={(row) => row.ID}
          localeText={gridLocale}
        />
      </Card>

      <Dialog open={!!selectedUser} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          İşlem Yetkilerini Düzenle
          <IconButton size="small" onClick={closeDialog} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          <Box sx={{ p: '10px 14px', borderRadius: '12px', border: '1px solid #e0e0e0', mb: 2 }}>
            <Box sx={{ fontSize: '14px', fontWeight: 700, color: '#263238' }}>{selectedUser?.ADSOYAD}</Box>
            <Box sx={{ fontSize: '11px', color: '#78909c' }}>Sicil No: {selectedUser?.SICILNO}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>İşlemler · {selected.size} seçili</div>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              <Button size="small" sx={{ ...lightButtonSx, fontSize: '11px', minWidth: 0 }} onClick={() => setSelected(new Set(componentItems.map((c) => c.id)))}>
                Tümünü Seç
              </Button>
              <Button size="small" sx={{ ...lightButtonSx, fontSize: '11px', minWidth: 0 }} onClick={() => setSelected(new Set())}>
                Temizle
              </Button>
            </Box>
          </Box>
          <SelectableTiles items={componentItems} selected={selected} original={original} onToggle={toggle} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Box sx={{ fontSize: '11px', color: changeCount ? '#e65100' : '#90a4ae', mr: 'auto', pl: 1 }}>
            {changeCount ? `${changeCount} değişiklik` : 'Değişiklik yok'}
          </Box>
          <Button sx={lightButtonSx} onClick={closeDialog} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={handleUpdateUserAuthorization}
            disabled={saving || changeCount === 0}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserBasedComponentAuth;
